import { motion } from "motion/react";
import { Link } from "react-router";
import {
  ClipboardList,
  FileText,
  FileSignature,
  CheckCircle,
  Rocket,
  ArrowRight,
  Calendar,
  MessageSquare,
  Settings,
} from "lucide-react";

export function Ablauf() {
  const steps = [
    {
      number: 1,
      icon: <ClipboardList className="w-8 h-8" />,
      title: "Fragebogen ausfüllen",
      duration: "5-10 Minuten",
      description:
        "Sie beantworten online unsere Fragen zu Ihrem Unternehmen, Ihrer Zielgruppe und Ihren Anforderungen. Das dauert nur wenige Minuten.",
      details: [
        "Unternehmensinformationen",
        "Zielgruppe definieren",
        "Gewünschte Leistungen",
        "Design-Präferenzen",
        "Bestehende Inhalte",
      ],
    },
    {
      number: 2,
      icon: <FileText className="w-8 h-8" />,
      title: "Entwurf erhalten",
      duration: "2-6 Tage",
      description:
        "Basierend auf Ihren Angaben erstellen wir einen professionellen Website-Entwurf. Sie erhalten einen geschützten Link zur Vorschau-Version per E-Mail.",
      details: [
        "Automatische Strukturerstellung",
        "Design-Layout passend zu Ihrer Marke",
        "Erste Textentwürfe",
        "Vorschau-Link und Entwurfs-Passwort per E-Mail",
        "Geschützte Entwurfsansicht im Browser",
      ],
    },
    {
      number: 3,
      icon: <FileSignature className="w-8 h-8" />,
      title: "Bestellung & Zahlung",
      duration: "5 Minuten",
      description:
        "Wenn der Entwurf für Sie passt, schließen Sie die Bestellung ab. Damit wird Ihr Projekt verbindlich übernommen und der Kundenbereich vorbereitet.",
      details: [
        "Bestellung direkt aus der geschützten Vorschau",
        "Zahlung des Grundpreises",
        "Monatlicher Beitrag ab dem Folgemonat",
      ],
    },
    {
      number: 4,
      icon: <CheckCircle className="w-8 h-8" />,
      title: "Zugang einrichten & Inhalte senden",
      duration: "4-6 Tage",
      description:
        "Nach erfolgreicher Bestellung legen Sie Ihr persönliches Passwort fest und übermitteln im Kundenbereich Inhalte, Bilder oder Zugangsdaten für die Umsetzung.",
      details: [
        "Passwort für den Kundenbereich vergeben",
        "Status im Dashboard verfolgen",
        "Dateien und Zugangsdaten hochladen",
        "Abrechnungen und nächste Schritte einsehen",
      ],
    },
    {
      number: 5,
      icon: <Rocket className="w-8 h-8" />,
      title: "Finalisierung & Livegang",
      duration: "1-2 Tage",
      description:
        "Sobald alle nötigen Inhalte vorliegen, setzen wir Ihre Website final um und schalten sie live. Danach übernehmen wir Hosting, Wartung und Support für Sie.",
      details: [
        "Website geht live",
        "Domain-Verknüpfung",
        "SSL-Zertifikat aktiviert",
        "Laufender Betrieb startet",
        "Support-Zugang aktiviert",
      ],
    },
  ];

  const faq = [
    {
      question: "Wie lange dauert der gesamte Prozess?",
      answer:
        "Den ersten Entwurf erhalten Sie in der Regel nach 2-6 Tagen. Bis zum Livegang hängt die Gesamtdauer vor allem davon ab, wann die Bestellung abgeschlossen ist und wie schnell Inhalte oder Zugangsdaten vorliegen.",
    },
    {
      question: "Wann erhalte ich Zugang zum Kundenbereich?",
      answer:
        "Den Kundenbereich richten wir nach erfolgreicher Bestellung ein. Dort vergeben Sie Ihr Passwort, verfolgen den Projektstatus und übermitteln Inhalte oder Zugangsdaten.",
    },
    {
      question: "Kann ich eigene Texte und Bilder liefern?",
      answer:
        "Ja, das ist sogar gewünscht. Nach der Bestellung können Sie Texte, Bilder und weitere Inhalte direkt über den Kundenbereich übermitteln. Wir können aber auch Textvorschläge machen und vorerst Stockfotos einbinden.",
    },
    {
      question: "Was ist, wenn ich keine Bilder habe?",
      answer:
        "Für die Entwurfsphase können wir Stockfotos verwenden. Wenn Sie möchten, kümmern wir uns anschließend gern um hochwertige Bilder Ihres Unternehmens. Dafür greifen wir auf unsere eigenen Fotografen oder, wenn nötig, unser langjährig gewachsenes Fotografen-Netzwerk zurück.",
    },
    {
      question: "Was ist, wenn ich mit dem Entwurf unzufrieden bin?",
      answer:
        "Dann bestellen Sie nicht direkt. Ein Vertrag kommt erst mit Ihrer Bestellung zustande. Wenn Sie grundsätzlich weiter mit uns arbeiten möchten, klären wir die nächsten Schritte individuell mit Ihnen.",
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl lg:text-6xl mb-6">
              So läuft der Prozess ab
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Von der Anfrage bis zur fertigen Website – transparent und
              strukturiert in 5 einfachen Schritten.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
	            {steps.map((step, index) => (
	              <motion.div
	                key={index}
	                initial={{ opacity: 0, y: 40 }}
	                whileInView={{ opacity: 1, y: 0 }}
	                viewport={{ once: true }}
	                transition={{ duration: 0.6, delay: index * 0.1 }}
	                className="relative"
	              >
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute left-[67px] top-32 bottom-0 w-0.5 bg-gray-200" />
                )}
                <div className="flex flex-col lg:flex-row gap-8">
                  {/* Icon Column */}
                  <div className="flex-shrink-0">
                    <div className="relative">
                      <div className="w-32 h-32 bg-black rounded-2xl flex items-center justify-center text-white">
                        {step.icon}
                      </div>
                      <div className="absolute -top-2 -right-2 w-10 h-10 bg-white border-4 border-black rounded-full flex items-center justify-center font-bold text-lg">
                        {step.number}
                      </div>
                    </div>
                  </div>

                  {/* Content Column */}
                  <div className="flex-1 lg:pt-4">
                    <div className="flex items-center gap-3 mb-3">
                      <h2 className="text-3xl font-semibold">{step.title}</h2>
                      <span className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        {step.duration}
                      </span>
                    </div>
                    <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                      {step.description}
                    </p>
                    <div className="bg-gray-50 rounded-xl p-6">
                      <h3 className="font-semibold mb-3 flex items-center gap-2">
                        <Settings className="w-5 h-5" />
                        Was passiert in diesem Schritt?
                      </h3>
	                      <ul className="space-y-2">
	                        {step.details.map((detail, idx) => (
	                          <li
	                            key={idx}
	                            className="flex items-start gap-2 text-sm text-gray-600"
	                          >
	                            <span className="w-1.5 h-1.5 bg-black rounded-full mt-2 flex-shrink-0" />
	                            {detail}
	                          </li>
	                        ))}
	                      </ul>
	                    </div>
	                  </div>
	                </div>

	              </motion.div>
	            ))}
	          </div>
	        </div>
	      </section>

	      {/* Communication */}
	      <section className="py-24 bg-gray-50">
	        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
	          <div className="grid lg:grid-cols-2 gap-12 items-center">
	            <motion.div
	              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <MessageSquare className="w-16 h-16 mb-6" />
              <h2 className="text-4xl mb-6">Direkte Kommunikation</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Die ersten Schritte laufen über Fragebogen, E-Mail und die geschützte
                Entwurfsvorschau. Nach erfolgreicher Bestellung erhalten Sie Zugang zum
                Kundenbereich für Status, Uploads und Abrechnung.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">
                    Geschützte Entwurfsansicht per Link und Passwort
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">
                    Passwortvergabe für den Kundenbereich nach Bestellung
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">
                    Status, Uploads und Projektfortschritt im Dashboard
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">
                    Rechnungen und Abbuchungen zentral an einem Ort
                  </span>
                </li>
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/images/laptop.webp"
                  alt="Team communication"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

	      {/* FAQ */}
	      <section className="py-24 bg-white">
	        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl mb-6">Häufige Fragen</h2>
          </motion.div>
	          <div className="space-y-4">
	            {faq.map((item, index) => (
	              <motion.div
	                key={index}
	                initial={{ opacity: 0, y: 20 }}
	                whileInView={{ opacity: 1, y: 0 }}
	                viewport={{ once: true }}
	                transition={{ duration: 0.6, delay: index * 0.1 }}
	                className="bg-gray-50 rounded-xl p-6 shadow-sm"
	              >
	                <h3 className="text-lg font-semibold mb-2">{item.question}</h3>
	                <p className="text-gray-600 leading-relaxed">{item.answer}</p>
	              </motion.div>
	            ))}
	          </div>
	        </div>
	      </section>

      {/* CTA */}
      <section className="py-24 bg-black text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl lg:text-5xl mb-6">
              Bereit, den Prozess zu starten?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Füllen Sie jetzt unseren Fragebogen aus und starten Sie Ihr Projekt
            </p>
            <Link
              to="/fragebogen"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-black hover:bg-gray-100 transition-all duration-300 group"
            >
              Fragebogen starten
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
